// @ts-strict-ignore
import { Component, EventEmitter, HostBinding, OnDestroy, OnInit, Output } from '@angular/core';
import { ProjectNavigationModel } from '../project-navigation.model';
import { ProjectNavigationService } from '../service/project-navigation.service';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { UserModel } from '@user/model/user.model';
import { UserUtils } from '@user/service/user-utils';
import { ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { AlertButton } from '@shared/alert/alertButton';
import { SavedProjectData, SaveProjectService } from '@project/components/project/project-save/save-project.service';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { AlertHelper } from '@shared/alert/alert.helper';
import { Subscription } from 'rxjs';
import { MarkingSystemProjectCreationService } from '@configuration/wizard/service/marking-system-project-creation.service';
import { UserSettings } from '@user/model/user-settings';
import { CaeProjectExportService } from '@project/components/import-export/service/cae-project-export.service';
import { ProjectDataService } from '@project/service/project-data.service';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';

@Component({
  standalone: false,
  templateUrl: './export-cae.component.html',
  selector: 'app-export-cae'
})
export class ExportCaeComponent implements OnInit, OnDestroy {
  @Output() public finish = new EventEmitter<void>();

  @HostBinding(`class.d-flex`) displayFlexClass = true;
  @HostBinding(`class.flex-column`) flexDirectionClass = true;

  public model: ProjectNavigationModel;

  public submitEnabled = false;
  public projectSavedHandlerSubscription: Subscription;
  private exportStarted = false;
  private _messageSaveProject: string;
  private _titleSaveProject: string;
  private _messageNoRightToSave: string;
  private _titleNoRightToSave: string;

  public constructor(
    private projectNavigationService: ProjectNavigationService,
    private saveProjectService: SaveProjectService,
    private projectDataService: ProjectDataService,
    private markingSystemProjectCreationService: MarkingSystemProjectCreationService,
    private caeProjectExportService: CaeProjectExportService,
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private userSettings: UserSettings,
    private localeResourceManager: LocaleResourceManager,
    private alertHelper: AlertHelper
  ) {}

  public ngOnInit() {
    this.model = new ProjectNavigationModel();
    this.model.currentModel = {
      importType: ProjectGroupVO.CAE_IMPORT_TYPE,
      lastSelectedGroupId: this.userSettings.lastSelectedProjectGroupId,
      lastSelectedProjectId: this.userSettings.lastSelectedProjectId,
      visibleSharedProjects: !this.userModel.user.isCustomer(),
      visibleHiddenProjects: true
    };

    this.projectNavigationService.openPreselectedProjectGroup(this.model);
  }

  public updateSubmitEnabled(): void {
    this.submitEnabled = this.isActionButtonEnabledExportMode();
  }

  private isActionButtonEnabledExportMode(): boolean {
    const selectedItem: ProjectHeadVO | ProjectGroupVO = this.model.currentSelectedItem;
    return selectedItem instanceof ProjectHeadVO || ProjectGroupUtils.isConfigurationGroup(selectedItem);
  }

  public ngOnDestroy() {
    this.removeProjectSaveHandler();
  }

  public async initializeCaeExport() {
    this.exportStarted = true;

    if (this.shouldSaveCurrentProject()) {
      if (UserUtils.hasPermissionToSaveProject(this.projectModel.user, this.projectModel.currentProject)) {
        await this.askToSaveProject();
      } else {
        this.showNoRightToSaveMessage();
      }
    } else {
      this.exportCae();
    }
    this.finish.emit();
  }

  private shouldSaveCurrentProject(): boolean {
    const selectedNode: ProjectHeadVO | ProjectGroupVO = this.model.currentSelectedItem;
    return (
      !this.projectModel.isNewConfiguration &&
      this.projectModel.isCurrentProjectChanged &&
      this.isItemInGroupNode(this.projectModel.currentProject.id, selectedNode)
    );
  }

  private isItemInGroupNode(itemId: number, currentNode: ProjectHeadVO | ProjectGroupVO): boolean {
    let result: boolean = false;
    if (currentNode instanceof ProjectGroupVO) {
      for (const projectHead of currentNode.projects) {
        if (this.isItemInGroupNode(itemId, projectHead)) {
          result = true;
          break;
        }
      }
      for (const childNode of currentNode.groups) {
        if (this.isItemInGroupNode(itemId, childNode)) {
          result = true;
          break;
        }
      }
    } else {
      result = currentNode.id === itemId;
    }
    return result;
  }

  private async askToSaveProject(): Promise<void> {
    this.loadExportLocalizedValues();
    const answer = await this.alertHelper.showAlert(this._titleSaveProject, this._messageSaveProject, {}, [AlertButton.YES, AlertButton.NO]);
    if (answer === AlertButton.YES) {
      this.addProjectSaveHandler();
      await this.saveProjectService.prepareSaveProject(SaveProjectService.PREPARE_PROJECT_SAVE, false, false);
    } else {
      this.exportCae();
    }
  }

  private addProjectSaveHandler() {
    this.projectSavedHandlerSubscription = this.saveProjectService.savedProject$.subscribe((projectSavedData) => this.savedProject(projectSavedData))
  }

  private removeProjectSaveHandler() {
    if (this.projectSavedHandlerSubscription) {
      this.projectSavedHandlerSubscription.unsubscribe();
    }
  }

  private async savedProject(data: SavedProjectData): Promise<void> {
    if (data.success) {
      if (this.userModel.user && this.userModel.user.hasPermission("LOAD_PROJECTS") && !this.model.compositeCheckBoxValue) {
        this.projectDataService
          .getLatestProjectVersionNumber(this.projectModel.currentProject.id)
          .toPromise()
          .then((currentVersion) => {
            this.projectModel.latestProjectVersionNumber = currentVersion;
          });
      }
      await this.markingSystemProjectCreationService.updateMarkingSystemPredefinedAreas();

      if (this.exportStarted) {
        this.removeProjectSaveHandler();
        await this.initializeCaeExport();
      }

      this.finish.emit();
    }
  }

  private showNoRightToSaveMessage(): void {
    this.loadExportLocalizedValues();
    this.alertHelper.showAlert(this._titleNoRightToSave, this._messageNoRightToSave, {}, [AlertButton.OK, AlertButton.CANCEL]).then((answer) => {
      this.exportCae();
    });
  }

  private exportCae(): void {
    if (this.exportStarted) {
      this.exportStarted = false;
      const selectedNode: ProjectHeadVO | ProjectGroupVO = this.model.currentSelectedItem;
      if (!selectedNode) {
        return;
      }
      let selectedGroupId: number = null;
      if (selectedNode instanceof ProjectGroupVO) {
        selectedGroupId = selectedNode.id;
      } else {
        selectedGroupId = selectedNode.projectGroup.id;
      }
      this.userSettings.setUserSettingValue("lastSelectedProjectGroupId", selectedGroupId);
      this.userSettings.saveUserSettings();

      const selectedId: number = selectedNode.id;
      const selectedName: string = selectedNode instanceof ProjectGroupVO ? selectedNode.groupName : (selectedNode as ProjectVO).name;
      const data = { selectedId, selectedName };

      if (selectedNode instanceof ProjectGroupVO) {
        this.caeProjectExportService.exportProjectGroupToCae(data);
      } else {
        this.caeProjectExportService.exportProjectToCae(data);
      }
    }
  }

  private loadExportLocalizedValues(): void {
    this._messageSaveProject = this.localeResourceManager.getValueByKey('caeExport.message.save_project');
    this._titleSaveProject = this.localeResourceManager.getValueByKey('projectNavigation.message_title.save_project');
    this._messageNoRightToSave = this.localeResourceManager.getValueByKey('caeExport.message.no_right_to_save');
    this._titleNoRightToSave = this.localeResourceManager.getValueByKey('projectNavigation.message_title.no_right_to_save');
  }

  public cancel() {
    this.finish.emit();
  }
}
